import React, { useEffect, useMemo, useRef, useState } from "react";
import { useTranslation } from "react-i18next";
import { Button, Popover, PopoverContent, PopoverTrigger, Spinner } from "@nextui-org/react";
import { BsCheck, BsLock, BsLockFill, BsPlus } from "react-icons/bs";
import { IoSearch } from "react-icons/io5";
import { MdFormatListBulleted } from "react-icons/md";

import myAxios from "../../../utils/axiosWrapper";
import getConfig from "../../../conf/config";
import { normalizeForSearch } from "../../../utils/stringUtils";

// Above this many lists, scanning becomes tedious: show the search field.
// Same threshold as the bulk menu in components/MAddToListsMenu.
const SEARCH_THRESHOLD = 7;

/**
 * Add/remove the call from lists, straight from the call modal.
 *
 * Every toggle is saved on the spot — no separate "save" step — because the
 * previous two-menu flow (open the actions menu, open the lists menu, tick,
 * reopen the actions menu, save) took five clicks to file one call.
 */
const ListsMenu = ({ call_id, lists, on_lists_changed }) => {
  const { t } = useTranslation();

  const [isDisplayed, setIsDisplayed] = useState(false);
  const [searchQuery, setSearchQuery] = useState("");
  const [inputName, setInputName] = useState("");
  const [isNewListPrivate, setIsNewListPrivate] = useState(false);
  const [isCreating, setIsCreating] = useState(false);
  const [pendingListIds, setPendingListIds] = useState([]);
  const [hasSaveError, setHasSaveError] = useState(false);

  const searchInputRef = useRef(null);
  // The selection the server is being told about, readable synchronously so
  // two quick toggles build on each other instead of racing.
  const selectionRef = useRef([]);
  // Toggles are chained so their requests land in the order they were clicked.
  const saveQueueRef = useRef(Promise.resolve());
  // Number of toggles still in flight; the store is only refreshed once the
  // last one settles, so a burst of clicks costs a single refetch.
  const inFlightCountRef = useRef(0);
  const isSavingRef = useRef(false);

  const [selectedListsIds, setSelectedListsIds] = useState([]);

  const listsContainingCall = useMemo(
    () => lists.filter((list) => list.call_ids?.includes(call_id)).map((list) => list.id),
    [lists, call_id]
  );

  const isSaving = pendingListIds.length > 0 || isCreating;

  useEffect(() => {
    isSavingRef.current = isSaving;
  }, [isSaving]);

  const applySelection = (ids) => {
    selectionRef.current = ids;
    setSelectedListsIds(ids);
  };

  // Re-sync whenever the store reports new data, but never on top of an
  // in-flight toggle: the optimistic state is ahead of what the store knows.
  // `isSaving` is read through a ref on purpose — depending on it would re-run
  // this the instant a save settles, i.e. before the refetch has landed, and
  // flip the checkbox back for a frame.
  useEffect(() => {
    if (isSavingRef.current) return;

    applySelection(listsContainingCall);
  }, [listsContainingCall]);

  const isSearchDisplayed = lists.length >= SEARCH_THRESHOLD;

  useEffect(() => {
    if (!isDisplayed) {
      setSearchQuery("");
      setInputName("");
      setIsNewListPrivate(false);
      setHasSaveError(false);
      return;
    }

    if (isSearchDisplayed) searchInputRef.current?.focus();
  }, [isDisplayed, isSearchDisplayed]);

  const normalizedQuery = normalizeForSearch(searchQuery);
  const filteredLists =
    isSearchDisplayed && normalizedQuery
      ? lists.filter((list) => normalizeForSearch(list.name).includes(normalizedQuery))
      : lists;

  const onToggleList = (list) => {
    const isSelected = selectionRef.current.includes(list.id);
    const nextIds = isSelected
      ? selectionRef.current.filter((listId) => listId !== list.id)
      : [...selectionRef.current, list.id];

    setHasSaveError(false);
    applySelection(nextIds);
    setPendingListIds((previous) => [...previous, list.id]);
    inFlightCountRef.current += 1;

    saveQueueRef.current = saveQueueRef.current
      .then(() =>
        myAxios.put(`${getConfig().API_URL}/audio/api/v1/lists/update_call_lists/${call_id}`, {
          lists_ids: nextIds,
        })
      )
      .catch((error) => {
        console.error("Error updating call lists:", error);
        setHasSaveError(true);
      })
      .finally(() => {
        setPendingListIds((previous) => previous.filter((listId) => listId !== list.id));

        inFlightCountRef.current -= 1;
        // Last one out refreshes: the store becomes the source of truth again.
        if (inFlightCountRef.current === 0) on_lists_changed();
      });
  };

  const onCreateListWithCall = async (event) => {
    event.preventDefault();

    const listName = inputName.trim();
    if (!listName || isCreating) return;

    setHasSaveError(false);
    setIsCreating(true);

    try {
      // Creating with the call already in it, so a brand new list is one step.
      await myAxios.post(`${getConfig().API_URL}/audio/api/v1/lists`, {
        list_name: listName,
        call_ids: [call_id],
        is_private: isNewListPrivate,
      });
      setInputName("");
      setIsNewListPrivate(false);
      on_lists_changed();
    } catch (error) {
      console.error("Error creating list:", error);
      setHasSaveError(true);
    }

    setIsCreating(false);
  };

  const renderTrigger = () => (
    <Button
      isIconOnly
      size="sm"
      variant="bordered"
      className="border-gray-200 relative overflow-visible"
      aria-label={t("Ajouter à une liste")}
      title={t("Ajouter à une liste")}
    >
      <MdFormatListBulleted size={16} />
      {selectedListsIds.length > 0 && (
        <span className="absolute -top-1.5 -right-1.5 min-w-[16px] h-[16px] px-1 rounded-full bg-primary text-white text-[10px] leading-[16px] text-center">
          {selectedListsIds.length}
        </span>
      )}
    </Button>
  );

  const renderPanel = () => (
    <div className="bg-white rounded-lg p-2 text-[#33403F] w-[260px]">
      {isSearchDisplayed && (
        <div className="relative mb-1.5">
          <IoSearch
            size={14}
            color="#737373"
            className="absolute left-[8px] top-1/2 -translate-y-1/2 pointer-events-none"
          />
          <input
            ref={searchInputRef}
            type="text"
            value={searchQuery}
            placeholder={t("Rechercher une liste")}
            onChange={(event) => setSearchQuery(event.target.value)}
            className="w-full text-sm rounded-md border-1 border-solid border-[#E5E5E5] bg-white py-1 pl-[28px] pr-[8px] outline-none focus:border-primary"
          />
        </div>
      )}

      <div className="max-h-[260px] overflow-y-auto">
        {filteredLists.length === 0 ? (
          <div className="py-1.5 px-1 text-sm text-[#737373]">
            {lists.length === 0 ? t("Aucune liste") : t("Aucun résultat")}
          </div>
        ) : (
          filteredLists.map((list) => {
            const isSelected = selectedListsIds.includes(list.id);
            const isPending = pendingListIds.includes(list.id);

            return (
              <button
                key={list.id}
                type="button"
                onClick={() => onToggleList(list)}
                className="w-full flex items-center gap-2 px-1.5 py-2 rounded-md text-left hover:bg-gray-100"
              >
                <span
                  className={`w-4 h-4 shrink-0 rounded border flex items-center justify-center ${
                    isSelected ? "bg-primary border-primary text-white" : "border-gray-300"
                  }`}
                >
                  {isSelected && <BsCheck size={14} />}
                </span>
                <span className="text-sm truncate flex-1">{list.name}</span>
                {list.is_private && (
                  <BsLockFill
                    size={11}
                    className="shrink-0 text-[#737373]"
                    title={t("Liste privée : visible uniquement par vous")}
                  />
                )}
                {isPending && <Spinner size="sm" />}
              </button>
            );
          })
        )}
      </div>

      {hasSaveError && (
        <div className="px-1.5 pt-1 text-xs text-danger">{t("La sauvegarde a échoué")}</div>
      )}

      <form
        onSubmit={onCreateListWithCall}
        className="flex items-center gap-1 mt-2 pt-2 border-t border-gray-100"
      >
        <BsPlus
          size={20}
          className="shrink-0 text-[#737373]"
        />
        <input
          className="min-w-0 flex-1 text-sm outline-none border-0 bg-transparent"
          placeholder={t("Nouvelle liste")}
          value={inputName}
          onChange={(event) => setInputName(event.target.value)}
        />
        <Button
          isIconOnly
          size="sm"
          variant="light"
          className={`shrink-0 min-w-0 w-7 h-7 ${
            isNewListPrivate ? "text-primary" : "text-[#A3A3A3]"
          }`}
          aria-pressed={isNewListPrivate}
          aria-label={t("Liste privée")}
          title={
            isNewListPrivate
              ? t("Liste privée : visible uniquement par vous")
              : t("Liste partagée : visible par l'équipe")
          }
          onPress={() => setIsNewListPrivate((previous) => !previous)}
        >
          {isNewListPrivate ? <BsLockFill size={13} /> : <BsLock size={13} />}
        </Button>
        <Button
          type="submit"
          size="sm"
          variant="light"
          color="primary"
          className="shrink-0 min-w-0 px-2"
          isDisabled={inputName.trim().length === 0}
          isLoading={isCreating}
        >
          {t("Créer")}
        </Button>
      </form>
    </div>
  );

  return (
    // NextUI's Popover, not MFloatingComponent: the latter's outside-click
    // handler ignores anything inside a [role="dialog"], so within the call
    // modal the panel could never be dismissed.
    <Popover
      placement="bottom-end"
      isOpen={isDisplayed}
      onOpenChange={setIsDisplayed}
    >
      <PopoverTrigger>{renderTrigger()}</PopoverTrigger>
      <PopoverContent className="p-0">{renderPanel()}</PopoverContent>
    </Popover>
  );
};

export default ListsMenu;
